// Server-side HTML renderer for LegalDoc — used at build time by each UI
// package's generator script to emit crawler-friendly mirrors of the
// privacy + terms pages.
//
// Pure function: same input → same output. No filesystem, no env access.
// The caller decides where to write, what canonical URL to advertise, etc.
// Inline styles only — these files have to render readably for crawlers that
// don't fetch external stylesheets. Kept small on purpose; the canonical
// rich rendering still lives at the React route.
const PAGE_STYLES = `
  :root { color-scheme: light dark; }
  * { box-sizing: border-box; }
  body {
    margin: 0;
    padding: 2rem 1rem;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
    line-height: 1.6;
    color: #1a1b1e;
    background: #fff;
    -webkit-font-smoothing: antialiased;
  }
  @media (prefers-color-scheme: dark) {
    body { color: #e7e9ee; background: #0f1115; }
    a { color: #74b9ff; }
    .meta { color: #909296; }
  }
  .container { max-width: 48rem; margin: 0 auto; }
  header { margin-bottom: 2rem; }
  h1 { font-size: 2rem; margin: 0 0 0.5rem; font-weight: 600; }
  .meta { color: #6b7280; font-size: 0.95rem; }
  section { margin-bottom: 2rem; }
  h2 { font-size: 1.25rem; margin: 0 0 0.75rem; font-weight: 600; }
  p { margin: 0.5rem 0; }
  ul { margin: 0.5rem 0; padding-left: 1.5rem; }
  li { margin: 0.25rem 0; }
  .subheading { font-weight: 600; margin: 1rem 0 0.5rem; }
  a { color: #1971c2; text-decoration: underline; }
  a:hover { text-decoration: none; }
  nav.footer-nav {
    margin-top: 3rem;
    padding-top: 1.5rem;
    border-top: 1px solid #e5e7eb;
    font-size: 0.9rem;
  }
  @media (prefers-color-scheme: dark) {
    nav.footer-nav { border-top-color: #2c2e33; }
  }
`;
// HTML escape — only the four characters that can break out of text/attr context.
const esc = (s) => s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
const renderRun = (run) => {
    switch (run.kind) {
        case "text":
            return esc(run.text);
        case "strong":
            return `<strong>${esc(run.text)}</strong>`;
        case "em":
            return `<em>${esc(run.text)}</em>`;
        case "link": {
            const target = run.external ? ` target="_blank" rel="noopener noreferrer"` : "";
            return `<a href="${esc(run.href)}"${target}>${esc(run.text)}</a>`;
        }
    }
};
const renderRuns = (runs) => runs.map(renderRun).join("");
const renderBlock = (block) => {
    switch (block.kind) {
        case "p":
            return `<p>${renderRuns(block.runs)}</p>`;
        case "list":
            return `<ul>${block.items.map((runs) => `<li>${renderRuns(runs)}</li>`).join("")}</ul>`;
        case "subheading":
            return `<p class="subheading">${esc(block.text)}</p>`;
    }
};
/** "Before you connect" → "before-you-connect": a link target, e.g. /docs#help as a support URL */
export const sectionId = (title) => title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
const renderSection = (section) => [
    `<section id="${sectionId(section.title)}">`,
    `<h2>${esc(section.title)}</h2>`,
    ...section.blocks.map(renderBlock),
    `</section>`,
].join("\n  ");
// First paragraph's plain-text content becomes the meta description.
const firstParagraphText = (doc) => {
    const firstParagraph = doc.sections
        .flatMap((section) => section.blocks)
        .find((block) => block.kind === "p");
    if (!firstParagraph)
        return "";
    return firstParagraph.runs
        .map((run) => ("text" in run ? run.text : ""))
        .join("")
        .replace(/\s+/g, " ")
        .trim()
        .slice(0, 200);
};
const DEFAULT_FOOTER_LINKS = [
    { href: "/", label: "Home" },
    { href: "/privacy", label: "Privacy Policy" },
    { href: "/terms", label: "Terms of Service" },
];
export function renderLegalDocToHtml(doc, opts) {
    const description = firstParagraphText(doc);
    const footerLinks = opts.footerLinks ?? DEFAULT_FOOTER_LINKS;
    const footerHtml = footerLinks.map((link) => `<a href="${esc(link.href)}">${esc(link.label)}</a>`).join(" · ");
    return `<!doctype html>
<html lang="en">
<head>
<meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>${esc(doc.title)} — ${esc(opts.brandSuffix)}</title>
<meta name="description" content="${esc(description)}">
<link rel="canonical" href="${esc(opts.canonicalUrl)}">
<link rel="icon" type="image/svg+xml" href="/favicon.svg">
<style>${PAGE_STYLES}</style>
</head>
<body>
<main class="container">
<header>
<h1>${esc(doc.title)}</h1>
<p class="meta">Last updated: ${esc(doc.lastUpdated)}</p>
</header>
${doc.sections.map(renderSection).join("\n")}
<nav class="footer-nav">
${footerHtml}
</nav>
</main>
</body>
</html>
`;
}
